import { FC, useEffect, useState } from "react"
import classNames from "classnames"
import dayjs, { Dayjs } from "dayjs"
import Header from "./Header"
import Loader from "@/components/Loader"
import LogCard from "@/components/LogCard"
import { Log, LogSource } from "@/types/logs"
import { getErrorMessage, localizeDate } from "@/utils/index"
import { formatCardVariant, formatLog } from "@/utils/logs"

import "./index.scss"

interface LogPanelProps {
   source?: LogSource
   isExpanded: boolean
   name: string
   title: string
   logData: Log[] | null
   loading: boolean
   error: number | null
   timezone: string
   getData: (selectedDate: string) => void
   onClose: () => void
   onExpand?: () => void
}

const LogPanel: FC<LogPanelProps> = ({
   source = LogSource.CELL,
   isExpanded,
   name,
   title,
   loading,
   logData,
   error,
   timezone,
   getData,
   onClose,
   onExpand,
}) => {
   const [selectedDate, setSelectedDate] = useState<Dayjs | null>(() =>
      dayjs().tz(timezone)
   )
   const [logsData, setLogsData] = useState<Log[]>([])

   useEffect(() => {
      if (!selectedDate) return
      getData(selectedDate.format("YYYY-MM-DD"))
   }, [selectedDate, getData])

   useEffect(() => {
      if (loading) {
         setLogsData([])
         return
      }
      if (logData) {
         setLogsData(formatLog(logData))
      }
   }, [loading, logData])

   return (
      <>
         <div
            className={classNames("log-panel__backdrop", {
               "log-panel__backdrop--visible": isExpanded,
            })}
            onClick={onClose}
         />
         <div
            className={classNames("log-panel", {
               "log-panel--expanded": isExpanded,
            })}
         >
            {loading ? (
               <Loader />
            ) : error ? (
               <span className="log-panel--error">
                  {getErrorMessage(error)}
               </span>
            ) : (
               <>
                  <Header
                     title={title}
                     isExpanded={isExpanded}
                     count={logsData?.length || 0}
                     name={name}
                     selectedDate={selectedDate!}
                     timezone={timezone}
                     onDateChange={setSelectedDate}
                     onExpand={onExpand}
                     onClose={onClose}
                  />
                  {logsData && logsData.length > 0 ? (
                     <>
                        {logsData.map((log, i) => (
                           <LogCard
                              key={i}
                              {...log}
                              source={source}
                              timezone={timezone}
                              cardVariant={formatCardVariant(log.logType)}
                           />
                        ))}
                     </>
                  ) : (
                     <span className="log-panel--empty">
                        No logs found on{" "}
                        {selectedDate && localizeDate(selectedDate, timezone)}
                     </span>
                  )}
               </>
            )}
         </div>
      </>
   )
}

export default LogPanel
